<template>
    <div class="n3-worker-table pt-4">
        <div class="filters-container mb-4">
             <div class="d-flex flex-wrap align-center mb-2">
                <span class="mr-2 grey--text text--darken-1">Estado:</span>
                <v-chip
                    filter
                    :input-value="showApproved"
                    @click="showApproved = !showApproved"
                    color="success"
                    class="mr-2"
                    outlined
                    small
                >
                    Aprobados
                </v-chip>
                <v-chip
                    filter
                    :input-value="showPending"
                    @click="showPending = !showPending"
                    color="primary"
                    outlined
                    small
                >
                    Pendientes
                </v-chip>
             </div>
             <div class="d-flex flex-wrap align-center" v-if="availableMonths.length > 0">
                <span class="mr-2 grey--text text--darken-1">Meses:</span>
                <v-chip-group
                    v-model="selectedMonths"
                    column
                    multiple
                >
                    <v-chip
                        v-for="month in availableMonths"
                        :key="month.value"
                        :value="month.value"
                        filter
                        outlined
                        small
                    >
                        {{ month.text }}
                    </v-chip>
                </v-chip-group>
             </div>
        </div>
        <h4>Anticipos del Año {{ selectedYear }}</h4>
        <div v-if="loading" class="loading-state">
            Cargando registros...
        </div>
        <div v-else-if="error" class="error-state">
            {{ error }}
        </div>
        <table v-else class="n3-table">
            <thead>
                <tr>
                    <th v-if="workCenterId">Trabajador</th>
                    <th>Mes</th>
                    <th>Anticipo Quincenal</th>
                    <th>Estado</th>
                    <th v-if="showObservations">Observaciones</th>
                    <th>Acciones</th>
                    <th v-if="showDetails">Detalles</th>
                </tr>
            </thead>
            <tbody v-for="entry in filteredEntries" :key="entry.id">
                    <tr :key="'main-' + entry.id">
                        <td v-if="workCenterId">
                            <div class="font-weight-bold">{{ entry.worker ? entry.worker.name : '' }} {{ entry.worker ? entry.worker.lastName1 : '' }}</div>
                            <div class="caption grey--text">{{ entry.worker ? entry.worker.idDocument : '' }}</div>
                        </td>
                        <td>{{ getMonthName(entry.month) }}</td>
                        <td>{{ formatCurrency(entry.amount) }}</td>
                        <td class="text-center">
                            <v-chip
                                small
                                :color="entry.approved ? 'success' : 'primary'"
                                text-color="white"
                            >
                                {{ entry.approved ? 'Aprobado' : 'Pendiente' }}
                            </v-chip>
                        </td>
                        <td v-if="showObservations">{{ entry.information }}</td>
                        <td class="text-center">
                            <edit-n3-entry
                              :entry="entry"
                              :isAdmin="isAdmin"
                              :workerId="workerId"
                              @entryEdited="fetchEntries"
                            />
                        </td>
                        <td class="text-center" v-if="showDetails">
                            <button class="btn-toggle" @click="toggleDetails(entry.id)">
                                {{ isExpanded(entry.id) ? '▲' : '▼' }}
                            </button>
                        </td>
                    </tr>
                    <tr v-if="showDetails && isExpanded(entry.id)" :key="'details-' + entry.id" class="details-row">
                        <td :colspan="columnCount">
                            <div class="details-content">
                                <p><strong>Entrada Admin:</strong> {{ entry.adminEntry ? 'Sí' : 'No' }}</p>
                                <p><strong>Fecha Creación:</strong> {{ formatDate(entry.creationDate) }}</p>
                                <p><strong>ID:</strong> {{ entry.id }}</p>
                                <div class="mt-2" v-if="isSuperAdmin">
                                    <v-btn
                                        :color="entry.approved ? 'secondary' : 'primary'"
                                        @click="toggleApproval(entry)"
                                        :loading="entry.loading"
                                        class="white--text"
                                        rounded
                                    >
                                        <v-icon left>{{ entry.approved ? 'mdi-close' : 'mdi-check' }}</v-icon>
                                        {{ entry.approved ? 'Desaprobar' : 'Aprobar' }}
                                    </v-btn>
                                </div>
                            </div>
                        </td>
                    </tr>
            </tbody>
            <tbody v-if="filteredEntries.length === 0">
                <tr v-if="filteredEntries.length === 0">
                    <td :colspan="columnCount" class="text-center">No se encontraron registros para este año.
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import N3Service from '@/services/n3.service';
import EditN3Entry from './EditN3Entry.vue';

export default {
  name: 'N3WorkerTable',
  components: {
    EditN3Entry,
  },
  props: {
    workerId: {
      type: [Number, String],
      required: false,
    },
    workCenterId: {
      type: [Number, String],
      required: false,
    },
    selectedYear: {
      type: [Number, String],
      required: true,
    },
    showDetails: {
      type: Boolean,
      default: true,
    },
    showObservations: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      n3Entries: [],
      loading: false,
      error: null,
      monthNames: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
      ],
      expandedRows: [],
      showApproved: true,
      showPending: true,
      selectedMonths: [],
    };
  },
  watch: {
    workerId: 'fetchEntries',
    workCenterId: 'fetchEntries',
    selectedYear: 'fetchEntries',
  },
  computed: {
    columnCount() {
      let count = 3;
      if (this.showObservations) count += 1;
      count += 1; // Acciones column
      if (this.showDetails) count += 1;
      if (this.workCenterId) count += 1;
      return count;
    },
    availableMonths() {
      const months = [...new Set(this.n3Entries.map((e) => e.month))].sort((a, b) => a - b);
      return months.map((m) => ({ value: m, text: this.getMonthName(m) }));
    },
    filteredEntries() {
      return this.n3Entries.filter((entry) => {
        const statusMatch = (entry.approved && this.showApproved) || (!entry.approved && this.showPending);
        const monthMatch = this.selectedMonths.length === 0 || this.selectedMonths.includes(entry.month);
        return statusMatch && monthMatch;
      });
    },
    isAdmin() {
      return this.$store.getters['auth/hasRole'](['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_ENCARGADO']);
    },
    isSuperAdmin() {
      return this.$store.getters['auth/hasRole'](['ROLE_SUPER_ADMIN']);
    },
    availableN3() {
      return this.$store.state.availableN3 === 'true';
    },
  },
  mounted() {
    this.fetchEntries();
  },
  methods: {
    fetchEntries() {
      if (!this.workerId && !this.workCenterId) return;
      this.loading = true;
      this.error = null;

      let promise;
      if (this.workCenterId) {
        promise = N3Service.getWorkersN3EntriesInRange(this.workCenterId, this.selectedYear);
      } else {
        promise = N3Service.getN3EntriesInRange(this.workerId, this.selectedYear);
      }

      promise
        .then((data) => {
          this.n3Entries = data.map((entry) => ({ ...entry, loading: false }));
          this.$emit('entries-loaded', this.n3Entries);
        })
        .catch((err) => {
          console.error('Error obteniendo registros N3:', err);
          this.error = 'Hubo un error al cargar los datos.';
        })
        .finally(() => {
          this.loading = false;
        });
    },

    toggleApproval(entry) {
      // eslint-disable-next-line no-param-reassign
      entry.loading = true;
      const newValue = !entry.approved;
      const wId = this.workCenterId ? entry.worker.id : this.workerId;
      N3Service.approveN3Entry(wId, entry.id, newValue)
        .then(() => {
          // eslint-disable-next-line no-param-reassign
          entry.approved = newValue;
        })
        .catch((error) => console.error(error))
        .finally(() => {
          // eslint-disable-next-line no-param-reassign
          entry.loading = false;
        });
    },

    toggleDetails(id) {
      const index = this.expandedRows.indexOf(id);
      if (index > -1) {
        this.expandedRows.splice(index, 1);
      } else {
        this.expandedRows.push(id);
      }
    },

    isExpanded(id) {
      return this.expandedRows.includes(id);
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('es-ES');
    },

    getMonthName(monthNumber) {
      // Ajuste si el mes viene como número (1-12)
      if (monthNumber && monthNumber >= 1 && monthNumber <= 12) {
        return this.monthNames[monthNumber - 1];
      }
      return monthNumber;
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return '-';
      return `${parseFloat(value).toFixed(2)} €`;
    },
  },
};
</script>

<style scoped>
.n3-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
}
.n3-table th,
.n3-table td {
    border-bottom: 2px solid var(--colorBackgroundLight);
    padding: 8px;
    text-align: left;
}
.n3-table th {
    background-color: var(--colorBackgroundLight);
    font-weight: bold;
}
.text-center {
    text-align: center;
}
.loading-state,
.error-state {
    padding: 20px;
    text-align: center;
    color: var(--colorGreyTypography);
}
.error-state {
    color: #d32f2f;
}
.btn-toggle {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.2em;
}
.details-row {
    background-color: var(--colorBackgroundLight);
}
.details-content {
    padding: 10px;
    font-size: 0.9em;
}
</style>
